import { useRoutes } from '../../../../store'
import { Paper } from '../../../common/Form'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG, formatMs } from 'common/api/v1/helpers'
import { AdBreak, AdCreativeWithUrls, AdErrorTracking, AdTracking } from 'common/api/v1/types'
import Tooltip from '../../../common/Tooltip'
import Box from '@mui/material/Box'
import { styles } from '../styles'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { TableConfig } from '../../../common/Table'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import MuiTable from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import TableCell from '@mui/material/TableCell'
import ColumnHeader from '../../../common/Table/ColumnHeader'
import TableBody from '@mui/material/TableBody'
import Circle from '@mui/icons-material/FiberManualRecord'
import { ApplianceAd } from 'common/types'
import { Link } from '../../../common/Link'
import { AdCreativeThumbnail } from '../../../common/Thumbnail'
import ErrorIcon from '@mui/icons-material/Error'
import Chip from '@mui/material/Chip'

export const TrackingSection = ({ adBreak, adCreatives }: { adBreak: AdBreak; adCreatives: AdCreativeWithUrls[] }) => {
  const routes = useRoutes()

  type TrackingGroup = {
    adCreativeId: string
    podIndex: number
    adCreative?: AdCreativeWithUrls
    applianceAd?: ApplianceAd
    events: AdTracking[]
  }

  const trackingGroups =
    adBreak.trackingEvents?.reduce<Record<string, TrackingGroup>>((acc, event) => {
      const { adCreativeId, podIndex } = event
      const trackingGroupId = `${adCreativeId}-${podIndex}`

      if (!acc[trackingGroupId]) {
        const applianceAd = adBreak.playlist?.ads?.find((ad) => ad.id.endsWith(adCreativeId))
        const adCreative = adCreatives.find((creative) => creative.id === adCreativeId)
        acc[trackingGroupId] = { adCreativeId, podIndex, events: [], applianceAd, adCreative }
      }
      acc[trackingGroupId].events.push(event)
      return acc
    }, {}) ?? {}

  const flattenedTrackingGroups = Object.values(trackingGroups).map((group) => ({
    ...group,
    events: group.events.map((event) => ({
      ...event,
    })),
  }))

  type ColumnType = TableConfig<(typeof flattenedTrackingGroups)[number]>[number]

  const tableConfig = [
    {
      title: 'Tracking Events',
      getValue: ({ events }) => {
        const statusMessage = (event: AdTracking | AdErrorTracking) => {
          if (event.eventType === 'error') {
            if ('errorCode' in event && event.errorCode !== '[ERRORCODE]') {
              return (
                <>
                  {`${event.errorMessage || 'Unknown error'} (VAST error code:`} <strong>{event.errorCode}</strong>)
                </>
              )
            }

            return (
              <>
                <strong>Unknown error</strong>
                <br />
                {event.reportedAt ? `Reported: ${format(event.reportedAt, DATE_FORMAT_LONG)}` : ``}
              </>
            )
          }

          if (event.reportCount > 1) {
            return (
              <>
                <strong>{event.eventType}</strong> reported {event.reportCount} times
                <br />
                {event.reportedAt ? `Last reported: ${format(event.reportedAt, DATE_FORMAT_LONG)}` : ``}
              </>
            )
          }

          if (event.reportedAt) {
            return (
              <>
                <strong>{event.eventType}</strong> successfully reported
                <br />
                {event.reportedAt ? `Reported: ${format(event.reportedAt, DATE_FORMAT_LONG)}` : ``}
              </>
            )
          }

          return (
            <>
              <strong>{event.eventType}</strong> pending
            </>
          )
        }

        const Icon = ({ event }: { event: AdTracking | AdErrorTracking }) => {
          if (event.eventType === 'error') {
            return <Circle sx={{ color: 'error.main' }} />
          }

          if (event.reportCount > 1) {
            return <ErrorIcon sx={{ color: 'warning.main' }} />
          }

          if (event.reportedAt) {
            return <CheckCircleIcon sx={{ color: 'success.main' }} />
          } else {
            return <Circle fontSize="small" sx={{ color: 'grey' }} />
          }
        }

        return (
          <Stack direction={'row'} spacing={1}>
            {events.map((event) => {
              return (
                <Tooltip title={statusMessage(event)}>
                  <Box sx={{ ...styles.container, width: 20, height: 20 }} key={event.eventType}>
                    <Icon event={event} />
                  </Box>
                </Tooltip>
              )
            })}
          </Stack>
        )
      },
      props: {
        align: 'left',
        width: '100px',
      },
    },
    {
      title: '',
      getValue: ({ applianceAd }) => {
        if (applianceAd?.filler) {
          return (
            <Tooltip
              title={`A filler ad creative is used to fill gaps in ad pods and is not required to be tracked`}
              placement="top"
            >
              <Chip label="Filler" size="small" color="warning" />
            </Tooltip>
          )
        }

        return <></>
      },
    },
    {
      title: 'Thumbnail',
      getValue: ({ adCreative }) => {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 150 }}>
            <AdCreativeThumbnail adCreative={adCreative} />
          </Box>
        )
      },
    },
    {
      title: 'Ad Creative',
      getValue: ({ adCreative }) => {
        if (!adCreative) {
          return (
            <Tooltip title={'Ad Creative not found. It might have been deleted'} placement="top">
              <Typography component="div" variant="body2">
                -
              </Typography>
            </Tooltip>
          )
        }

        return (
          <>
            {adCreative.status === 'active' ? (
              <Link underline="hover" to={routes.adCreativesUpdate({ id: adCreative.id })}>
                {adCreative.name}
              </Link>
            ) : (
              <Typography component="div" variant="body2">
                {adCreative.name}
              </Typography>
            )}
          </>
        )
      },
    },
    {
      title: 'Duration',
      getValue: ({ applianceAd }) => {
        if (!applianceAd) {
          return (
            <Tooltip title={'Ad not found. It might have been deleted'} placement="top">
              <Typography component="div" variant="body2">
                -
              </Typography>
            </Tooltip>
          )
        }

        return (
          <Tooltip title={`${applianceAd.durationMs} ms`} placement="top">
            <Typography component="div" variant="body2">
              {formatMs(applianceAd.durationMs)}
            </Typography>
          </Tooltip>
        )
      },
    },
  ] as ColumnType[]

  return (
    <Paper title={'Tracking'}>
      {adBreak.trackingEvents?.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography>No registered tracking events.</Typography>
        </Box>
      ) : (
        <MuiTable>
          <TableHead>
            <TableRow>
              {tableConfig
                .filter((c) => c !== null)
                .map(({ title }, ind, { length }) => (
                  <TableCell
                    key={ind}
                    sx={styles.cell}
                    padding={ind === length - 1 ? 'none' : undefined}
                    data-table-column={title}
                  >
                    <ColumnHeader title={title} />
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {flattenedTrackingGroups.map((event: (typeof flattenedTrackingGroups)[number], i: number) => (
              <TableRow hover key={event.adCreativeId}>
                {tableConfig
                  .filter((c) => c !== null)
                  .map(({ getValue, props, title }) => (
                    <TableCell {...props} data-cell-name={`${title}-cell`} key={`${event.adCreativeId}-${title}`}>
                      <Typography component="div" variant="body2">
                        {getValue(event, i)}
                      </Typography>
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      )}
    </Paper>
  )
}
